import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { api } from '../services/api';

interface SupportRequest {
  id: string;
  message: string;
  created_at: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error' | 'rate-limited';

export function SupportPage() {
  const [message, setMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await api.vision.getSupportRequests();
      if (response.success && response.data) {
        setRequests(response.data.requests || []);
      }
    } catch {
      // silent fail for history
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 10 || trimmed.length > 1000) return;

    setSubmitStatus('submitting');
    setStatusMessage(null);

    try {
      const response = await api.vision.submitSupport(trimmed);

      if (response.success) {
        setSubmitStatus('success');
        setStatusMessage('Recebemos sua mensagem! Entraremos em contato pelo seu e-mail.');
        setMessage('');
        loadHistory();
        setTimeout(() => {
          setSubmitStatus('idle');
          setStatusMessage(null);
        }, 5000);
      } else if (response.error?.code === 'RATE_LIMITED') {
        setSubmitStatus('rate-limited');
        setStatusMessage(response.error.message || 'Limite diário atingido. Tente novamente amanhã.');
      } else {
        setSubmitStatus('error');
        setStatusMessage(response.error?.message || 'Erro ao enviar mensagem.');
      }
    } catch {
      setSubmitStatus('error');
      setStatusMessage('Erro de conexão. Tente novamente.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const charCount = message.trim().length;
  const isValid = charCount >= 10 && charCount <= 1000;

  return (
    <AppShell>
      <div className="space-y-6 max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-display text-2xl font-bold text-white">
            Suporte
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Envie um bug, sugestão ou dúvida.
          </p>
        </motion.div>

        {/* Form */}
        <Card delay={0.1}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Descreva o problema ou sugestão..."
            rows={5}
            maxLength={1000}
            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white placeholder-white/30 resize-none focus:outline-none focus:border-turquoise/50 transition-colors"
          />
          <div className="flex justify-between items-center mt-2">
            <span className={`text-xs ${charCount > 1000 ? 'text-coral' : charCount < 10 && charCount > 0 ? 'text-warning-orange' : 'text-white/30'}`}>
              {charCount}/1000
            </span>
            {charCount > 0 && charCount < 10 && (
              <span className="text-xs text-warning-orange">Mínimo 10 caracteres</span>
            )}
          </div>

          {/* Status message */}
          {statusMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-3 p-3 rounded-xl flex items-start gap-2 ${
                submitStatus === 'success'
                  ? 'bg-success-green/20 text-success-green'
                  : submitStatus === 'rate-limited'
                    ? 'bg-warning-orange/20 text-warning-orange'
                    : 'bg-coral/20 text-coral'
              }`}
            >
              {submitStatus === 'success' ? (
                <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <span className="text-sm">{statusMessage}</span>
            </motion.div>
          )}

          <Button
            onClick={handleSubmit}
            fullWidth
            isLoading={submitStatus === 'submitting'}
            disabled={!isValid || submitStatus === 'submitting' || submitStatus === 'rate-limited'}
            className="mt-4"
          >
            <Send className="w-4 h-4" />
            <span>Enviar</span>
          </Button>
        </Card>

        {/* History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-semibold text-white mb-3">Mensagens enviadas</h2>
          {isLoadingHistory ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : requests.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8">
              Nenhuma mensagem enviada ainda.
            </p>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <Card key={req.id} delay={0}>
                  <p className="text-white/80 text-sm whitespace-pre-wrap">{req.message}</p>
                  <div className="flex items-center gap-1.5 mt-2 text-white/30 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(req.created_at)}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </AppShell>
  );
}
